/* Control de EPP: registrar una entrega a un colaborador.
 *
 * Se elige el depósito, el colaborador y los ítems (solo los que tienen stock en
 * ese depósito). Si el colaborador tiene una entrega vigente del ítem cuya fecha
 * estimada de reposición todavía no llegó, es un retiro anticipado: se permite y
 * se registra el motivo (uno de la lista u "Otro" con texto). El backend lo vuelve
 * a verificar y responde 409 si falta algún motivo.
 */
import { IonButton, IonContent, IonIcon, IonPage } from "@ionic/react";
import { MenuItem, TextField } from "@material-ui/core";
import MuiAutocomplete from "@material-ui/lab/Autocomplete";
import { checkmarkOutline, trashOutline } from "ionicons/icons";
import { useEffect, useMemo, useState } from "react";
import Header from "src/components/Header";
import {
  AnticipadaInfo,
  BuscadorFuncionario,
  Cargando,
  EstadoChip,
  SelectorDeposito,
  useRolEpp,
  useToastEpp,
} from "src/components/epp/EppComunes";
import EppAPI, {
  AnticipadaSinMotivo,
  Funcionario,
  LineaEntrega,
  MotivoAnticipada,
  Saldo,
  etiquetaItem,
  fechaCorta,
  plural,
} from "src/services/epp";
import "./Epp.css";

// Valor del selector para el motivo "Otro" (texto libre).
const MOTIVO_OTRO = "otro";

interface Linea {
  clave: string;
  saldo: Saldo;
  cantidad: string;
  /** Id del motivo, MOTIVO_OTRO o "" sin elegir. */
  motivo: string;
  motivoOtro: string;
}

const hoyIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const claveSaldo = (s: Saldo) => `${s.ItemId}|${s.Talle}`;

const EppEntrega: React.FC = () => {
  const rol = useRolEpp();
  const toast = useToastEpp();

  const [depositoId, setDepositoId] = useState<number | "">("");
  const [stock, setStock] = useState<Saldo[]>([]);
  const [motivos, setMotivos] = useState<MotivoAnticipada[]>([]);
  const [funcionario, setFuncionario] = useState<Funcionario | null>(null);
  const [vigentes, setVigentes] = useState<LineaEntrega[] | null>(null);
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [observacion, setObservacion] = useState("");
  const [guardando, setGuardando] = useState(false);
  const [claveBuscador, setClaveBuscador] = useState(0);

  useEffect(() => {
    if (rol?.depositos.length && depositoId === "") setDepositoId(rol.depositos[0].Id);
  }, [rol, depositoId]);

  useEffect(() => {
    EppAPI.motivos().then(setMotivos).catch((err) => toast.error(err, "No se pudieron cargar los motivos"));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cargarStock = (id: number) =>
    EppAPI.stock(id)
      .then((s) => setStock(s.filter((x) => x.Cantidad > 0)))
      .catch((err) => toast.error(err, "No se pudo cargar el stock"));

  useEffect(() => {
    setLineas([]);
    if (depositoId) cargarStock(depositoId);
  }, [depositoId]); // eslint-disable-line react-hooks/exhaustive-deps

  const elegirFuncionario = async (f: Funcionario | null) => {
    setFuncionario(f);
    setVigentes(null);
    if (!f) return;
    try {
      const datos = await EppAPI.colaborador(f.cedula);
      setFuncionario({ ...f, ...datos.funcionario });
      setVigentes(datos.vigentes);
    } catch (err) {
      toast.error(err, "No se pudo consultar el colaborador");
      setFuncionario(null);
    }
  };

  const vigentePorItem = useMemo(() => {
    const mapa: Record<number, LineaEntrega> = {};
    (vigentes || []).forEach((v) => (mapa[v.ItemId] = v));
    return mapa;
  }, [vigentes]);

  /** Línea vigente cuyo plazo todavía no se cumplió: la nueva entrega es anticipada. */
  const anticipada = (itemId: number) => {
    const v = vigentePorItem[itemId];
    return v && v.FechaReposicion && v.FechaReposicion > hoyIso() ? v : null;
  };

  const disponibles = stock.filter(
    (s) => !lineas.some((l) => l.saldo.ItemId === s.ItemId)
  );

  const agregar = (s: Saldo | null) => {
    if (!s) return;
    setLineas((ls) => [...ls, { clave: claveSaldo(s), saldo: s, cantidad: "1", motivo: "", motivoOtro: "" }]);
  };

  const cambiar = (clave: string, cambios: Partial<Linea>) =>
    setLineas((ls) => ls.map((l) => (l.clave === clave ? { ...l, ...cambios } : l)));

  const limpiar = () => {
    setFuncionario(null);
    setVigentes(null);
    setLineas([]);
    setObservacion("");
    setClaveBuscador((k) => k + 1);
  };

  const confirmar = async () => {
    if (!depositoId || !funcionario) return;
    if (!lineas.length) return toast.aviso("Agregá al menos un ítem");
    for (const l of lineas) {
      const cantidad = Number(l.cantidad);
      if (!Number.isInteger(cantidad) || cantidad <= 0 || cantidad > l.saldo.Cantidad) {
        return toast.aviso(`Revisá la cantidad de ${etiquetaItem(l.saldo.Item, l.saldo.Talle)} (hay ${l.saldo.Cantidad})`);
      }
      if (anticipada(l.saldo.ItemId) && (!l.motivo || (l.motivo === MOTIVO_OTRO && !l.motivoOtro.trim()))) {
        return toast.aviso(`Indicá el motivo del retiro anticipado de ${l.saldo.Item}`);
      }
    }

    setGuardando(true);
    try {
      const res = await EppAPI.registrarEntrega({
        DepositoId: depositoId,
        Cedula: funcionario.cedula,
        Observacion: observacion.trim() || undefined,
        Lineas: lineas.map((l) => ({
          ItemId: l.saldo.ItemId,
          Talle: l.saldo.Talle,
          Cantidad: Number(l.cantidad),
          ...(anticipada(l.saldo.ItemId) && l.motivo
            ? l.motivo === MOTIVO_OTRO
              ? { MotivoOtro: l.motivoOtro.trim() }
              : { MotivoId: Number(l.motivo) }
            : {}),
        })),
      });
      const n = res.data?.anticipadas || 0;
      toast.ok(n ? `Entrega registrada, con ${plural(n, "retiro anticipado", "retiros anticipados")}` : "Entrega registrada");
      limpiar();
      cargarStock(depositoId);
    } catch (err: any) {
      const sinMotivo: AnticipadaSinMotivo[] | undefined = err?.response?.status === 409 ? err.response.data?.datos : undefined;
      if (sinMotivo?.length) {
        // El backend vio entregas que la pantalla no tenía: se recarga para pedir los motivos.
        elegirFuncionario(funcionario);
      }
      toast.error(err, "No se pudo registrar la entrega");
    } finally {
      setGuardando(false);
    }
  };

  if (!rol) {
    return (
      <IonPage>
        <Header titulo="Registrar entrega de EPP" />
        <IonContent><Cargando /></IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <Header titulo="Registrar entrega de EPP" />
      <IonContent id="epp-entrega">
        <div className="epp-page campos-mui">
          <div className="epp-card">
            <div className="epp-barra">
              <SelectorDeposito depositos={rol.depositos} valor={depositoId} onChange={setDepositoId} />
            </div>
            <BuscadorFuncionario key={claveBuscador} valor={funcionario} onSelect={elegirFuncionario} />
            {funcionario && (
              <div className="epp-meta">
                <span>CI {funcionario.cedula}</span>
                {funcionario.legajo && <span>Legajo {funcionario.legajo}</span>}
                <span>{funcionario.cargo}</span>
                <span>{funcionario.departamento}</span>
              </div>
            )}
          </div>

          {funcionario && vigentes === null && <Cargando />}

          {funcionario && vigentes && (
            <>
              <div className="epp-card">
                <p className="epp-card-titulo">EPP vigentes del colaborador</p>
                {vigentes.length === 0 && <div className="epp-meta">No tiene entregas registradas.</div>}
                {vigentes.map((v) => (
                  <div key={v.Id} className="epp-fila">
                    <div className="epp-fila-cabecera">
                      <span className="epp-item">{etiquetaItem(v.Item, v.Talle)}</span>
                      <EstadoChip estado={v.Estado} etiqueta={v.EstadoEtiqueta} />
                    </div>
                    <div className="epp-meta">
                      <span>Entregado {fechaCorta(v.Fecha)}</span>
                      <span>Reposición {fechaCorta(v.FechaReposicion)}</span>
                    </div>
                    {v.Anticipada && <AnticipadaInfo motivo={v.Motivo} estado={v.Estado} />}
                  </div>
                ))}
              </div>

              <div className="epp-card">
                <p className="epp-card-titulo">Ítems a entregar</p>
                <MuiAutocomplete
                  key={lineas.length}
                  options={disponibles}
                  getOptionLabel={(s) => `${etiquetaItem(s.Item, s.Talle)} · stock ${s.Cantidad}`}
                  onChange={(_, s) => agregar(s)}
                  noOptionsText={stock.length ? "Sin más ítems con stock" : "El depósito no tiene stock"}
                  renderInput={(params) => (
                    <TextField {...params} label="Agregar ítem" variant="outlined" size="small" fullWidth />
                  )}
                />
                {lineas.map((l) => {
                  const previa = anticipada(l.saldo.ItemId);
                  return (
                    <div key={l.clave} className="epp-fila">
                      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                        <div className="epp-item" style={{ flex: 1 }}>{etiquetaItem(l.saldo.Item, l.saldo.Talle)}</div>
                        <TextField
                          label="Cant."
                          type="number"
                          variant="outlined"
                          size="small"
                          style={{ width: 90 }}
                          value={l.cantidad}
                          inputProps={{ min: 1, max: l.saldo.Cantidad }}
                          onChange={(e) => cambiar(l.clave, { cantidad: e.target.value })}
                        />
                        <IonButton fill="clear" color="danger" onClick={() => setLineas((ls) => ls.filter((x) => x.clave !== l.clave))}>
                          <IonIcon icon={trashOutline} />
                        </IonButton>
                      </div>
                      <div className="epp-meta">
                        <span>
                          {l.saldo.VidaUtilDias
                            ? `Vida útil ${l.saldo.VidaUtilDias} días`
                            : "Sin vida útil definida"}
                        </span>
                        {l.saldo.Criterio && <span>{l.saldo.Criterio}</span>}
                      </div>
                      {previa && (
                        <>
                          <div className="epp-aviso alerta">
                            Retiro anticipado: recibió este EPP el {fechaCorta(previa.Fecha)} y la reposición
                            estimada es el {fechaCorta(previa.FechaReposicion)}. Indicá el motivo.
                          </div>
                          <TextField
                            select
                            label="Motivo (obligatorio)"
                            variant="outlined"
                            size="small"
                            fullWidth
                            value={l.motivo}
                            onChange={(e) => cambiar(l.clave, { motivo: String(e.target.value) })}
                          >
                            {motivos.map((m) => (
                              <MenuItem key={m.Id} value={String(m.Id)}>{m.Nombre}</MenuItem>
                            ))}
                            <MenuItem value={MOTIVO_OTRO}>Otro</MenuItem>
                          </TextField>
                          {l.motivo === MOTIVO_OTRO && (
                            <TextField
                              label="Describí el motivo"
                              variant="outlined"
                              size="small"
                              fullWidth
                              multiline
                              margin="dense"
                              value={l.motivoOtro}
                              inputProps={{ maxLength: 300 }}
                              onChange={(e) => cambiar(l.clave, { motivoOtro: e.target.value })}
                            />
                          )}
                        </>
                      )}
                    </div>
                  );
                })}
              </div>

              <div className="epp-card">
                <TextField
                  label="Observación (opcional)"
                  variant="outlined"
                  size="small"
                  fullWidth
                  multiline
                  value={observacion}
                  inputProps={{ maxLength: 300 }}
                  onChange={(e) => setObservacion(e.target.value)}
                />
              </div>

              <IonButton className="epp-boton-final" expand="block" onClick={confirmar} disabled={guardando || !lineas.length}>
                <IonIcon slot="start" icon={checkmarkOutline} />
                {guardando ? "Registrando..." : "Registrar entrega"}
              </IonButton>
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default EppEntrega;
